import {
  isNetworkFailure,
  OfflineQueuedError,
  OfflineUnavailableError,
} from '../shared/api/errors';
import { readCachedRead, writeCachedRead } from './cache';
import { isOnline } from './connectivity';
import { emitOffline, noteServedOffline } from './events';
import { enqueueWrite } from './outbox';
import { readPolicyFor, writePolicyFor } from './policy';
import type { OfflineRequestRuntime } from './runtime';
import { vaultUnlocked } from './vault';

/**
 * The rules that turn an ordinary API call into an offline-capable one.
 *
 * Two of them are worth stating plainly, because they are the ones that keep the books honest:
 *
 *   - **A read is copied on the way past, never invented.** What comes back offline is a response
 *     the server gave earlier, with the time it was given, and nothing is assembled locally.
 *   - **A write is only captured when there is provably no connection.** If the connection was
 *     up and the request failed in flight, the request is *not* queued: it may well have reached
 *     the server, and replaying it could post a second document. That case is reported as the
 *     failure it is, for a person to retry deliberately.
 */

export interface OfflineIdentity {
  readonly userId: string | null;
  readonly tenantId: string | null;
}

let identity: OfflineIdentity = { userId: null, tenantId: null };

/** Kept current by the offline provider from the session, and by the vault before sign-in. */
export function setOfflineIdentity(next: OfflineIdentity): void {
  identity = next;
}

export function offlineIdentity(): OfflineIdentity {
  return identity;
}

/** Asks the service worker to wake a window when the connection returns. */
async function requestBackgroundSync(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.ready;
    // `sync` is Chromium-only; elsewhere the provider's own `online` listener does the same job.
    const sync = (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })?.sync;
    await sync?.register('varza-outbox');
  } catch {
    // Nothing to recover: the queue is durable and the next foreground flush will find it.
  }
}

export function createOfflineInterceptor(): OfflineRequestRuntime {
  return {
    interceptRead<T>(path: string, send: () => Promise<T>): Promise<T> | null {
      const policy = readPolicyFor(path);
      if (policy === null) return null;

      const owner = policy.scope === 'user' ? identity.userId : identity.tenantId;
      // Without an owner there is nothing to file the copy under, and a tenant-scoped response
      // must never be stored where another tenant's request could match it.
      if (owner === null || owner === '') return null;

      /** The copy, or `null` when this page was never taken. */
      const fromDevice = async (): Promise<{ value: T } | null> => {
        const cached = await readCachedRead<T>(policy, owner, path);
        if (cached === null) return null;
        noteServedOffline(cached.storedAt);
        return { value: cached.data };
      };

      /** Tries the network and reports a failure to reach it as such. */
      const fromNetwork = async (): Promise<T> => {
        try {
          return await send();
        } catch (error) {
          if (isNetworkFailure(error)) throw new OfflineUnavailableError();
          throw error;
        }
      };

      return (async () => {
        if (!isOnline()) {
          const cached = await fromDevice();
          if (cached !== null) return cached.value;
          /*
           * There is nothing to show, so the network is tried even though the workspace believes
           * there is none. That is deliberate: "unreachable" is an inference from an earlier
           * failure, and a single blocked request — an extension, a proxy, one bad endpoint — must
           * not be able to strand the workspace on a stale copy with nothing left that would ever
           * discover the connection had returned. A success here says it has.
           */
          return fromNetwork();
        }

        try {
          const data = await send();
          // Not awaited: a device that cannot write its copy still has a good response to return.
          void writeCachedRead(policy, owner, path, data).catch(() => undefined);
          return data;
        } catch (error) {
          // The transport has already recorded that the server is unreachable; what is left to
          // decide here is whether the device can answer instead.
          if (!isNetworkFailure(error)) throw error;
          const cached = await fromDevice();
          if (cached === null) throw new OfflineUnavailableError();
          return cached.value;
        }
      })();
    },

    interceptWrite<T>(method: string, path: string, body: unknown): Promise<T> | null {
      // With a connection, a write goes to the server and the server decides. The queue exists
      // for the case where there is nothing to ask.
      if (isOnline()) return null;

      return (async (): Promise<never> => {
        const policy = writePolicyFor(method, path);
        if (policy === null) throw new OfflineUnavailableError();
        if (!vaultUnlocked()) throw new OfflineUnavailableError();
        if (identity.userId === null) throw new OfflineUnavailableError();

        const record = await enqueueWrite({
          userId: identity.userId,
          tenantId: identity.tenantId,
          policy,
          path,
          body,
        });

        emitOffline('queue-changed');
        void requestBackgroundSync();
        throw new OfflineQueuedError(record.id);
      })();
    },
  };
}
